<template>
  <component
    :is="tag"
    class="play-sticker"
    :class="[`play-sticker--${variant}`, `play-sticker--${size}`, { 'play-sticker--animated': animated }]"
    :style="{ '--st-rot': `${rotate}deg` }"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <!-- VUE3-MIGRATION: $listeners removed in Vue 3 (merged into $attrs).
       On migration delete `v-on="$listeners"` — v-bind="$attrs" forwards listeners too. -->
    <span
      v-if="$slots.icon"
      class="play-sticker__icon"
    ><slot name="icon" /></span>
    <span class="play-sticker__label"><slot /></span>
  </component>
</template>

<script>
/**
 * PlaySticker — the rotated "dopamine label" of the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors, so a palette
 * swap requires zero changes here. It's the playful pill that calls out a
 * benefit / badge ("⚡ One link runs your whole business").
 *
 * A tilted pill with a hard offset shadow. `rotate` sets the base angle (the
 * wobble animation negates it, so any angle stays balanced). `animated` toggles
 * the idle wobble — auto-disabled under prefers-reduced-motion.
 *
 * Renders as <span> by default; use `tag` for other elements.
 *
 * VUE3-MIGRATION: `v-on="$listeners"` in the template is the only Vue 2-ism
 * here. In Vue 3 $listeners is removed (merged into $attrs); drop that line
 * and v-bind="$attrs" keeps forwarding listeners. The props/slots contract is
 * already Vue 3-safe.
 */
export default {
  name: 'PlaySticker',
  inheritAttrs: false,
  props: {
    /** Color role: violet accent, main CTA, lime highlight, butter, or dark */
    variant: {
      type: String,
      default: 'secondary',
      validator: v => ['secondary', 'primary', 'highlight', 'accent', 'ink'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    /** Base tilt angle in degrees (negated by the wobble keyframe) */
    rotate: {
      type: Number,
      default: -2,
    },
    /** Idle wobble animation (disabled automatically under reduced-motion) */
    animated: {
      type: Boolean,
      default: true,
    },
    tag: {
      type: String,
      default: 'span',
    },
  },
}
</script>

<style lang="scss" scoped>
.play-sticker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-display);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  border-radius: var(--radius-pill);
  transform: rotate(var(--st-rot, -2deg));
  -webkit-tap-highlight-color: transparent;

  &__icon {
    display: inline-flex;
    align-items: center;
    font-size: 1.1em;
  }

  /* ── Sizes ── */
  &--sm { padding: 6px 13px;  font-size: 12px; }
  &--md { padding: 8px 16px;  font-size: 13.5px; }
  &--lg { padding: 10px 20px; font-size: 15px; }

  /* ── Variants — colored fill + hard offset shadow in the role's -strong slot ── */
  &--secondary { background: var(--c-secondary); color: var(--c-secondary-on); box-shadow: 0 5px 0 var(--c-secondary-strong); }
  &--primary   { background: var(--c-primary);   color: var(--c-primary-on);   box-shadow: 0 5px 0 var(--c-primary-strong); }
  &--highlight { background: var(--c-highlight);  color: var(--c-highlight-on); box-shadow: 0 5px 0 var(--c-highlight-strong); }
  &--accent    { background: var(--c-accent);     color: var(--c-accent-on);    box-shadow: 0 5px 0 var(--c-accent-strong); }
  &--ink       { background: var(--surface-ink);  color: var(--text-on-ink);    box-shadow: 0 5px 0 var(--border-ink); }

  /* ── Idle wobble ── */
  &--animated { animation: play-sticker-wob 4s ease-in-out infinite; }
}

@keyframes play-sticker-wob {
  0%, 100% { transform: rotate(var(--st-rot, -2deg)); }
  50%      { transform: rotate(calc(var(--st-rot, -2deg) * -1)) translateY(-3px); }
}

@media (prefers-reduced-motion: reduce) {
  .play-sticker--animated { animation: none; }
}
</style>
